import React, { useRef, forwardRef, useState } from 'react';
import { FaUpload, FaCloud } from 'react-icons/fa';
import './InputFile.scss';
import Modal from './../Modal';
import useModal from '../../customHooks/useModal';
import S3Item from './S3Item';
import Button from '../Button';
import translations from '../../translations/de';
import API from '../../api/users';

const InputFile = forwardRef(
    (
        {
            label = '',
            error = false,
            resetValue,
            disabled,
            pathToCloudFolder,
            readOnly,
            preferredFileSize = translations.imageSizeLabel,
            squareResolution = false,
            // Restrict the picker to these extensions (e.g. ['png', 'jpg', 'jpeg']
            // for push images: the phones cannot render SVG in a notification).
            // Applies to local uploads and to the cloud picker; the text field
            // itself stays free so an existing value can still be edited.
            allowedExtensions = null,
            formatHint = null,
            ...restProps
        },
        ref,
    ) => {
        const inputFileRef = useRef();
        const modalRef = useRef(null);
        const [modal, setModal] = useModal(false, modalRef);
        const [uploads, setUploads] = useState(null);
        const [loading, setLoading] = useState(false);
        const [formatError, setFormatError] = useState(null);

        const extensionOf = (name) => (name || '').split('?')[0].split('.').pop().toLowerCase();
        const isAllowed = (name) =>
            !allowedExtensions || allowedExtensions.includes(extensionOf(name));
        const accept = allowedExtensions
            ? allowedExtensions.map((ext) => `.${ext}`).join(',')
            : 'image/*';

        const handleUpload = () => {
            if (inputFileRef.current.files[0]) {
                if (!isAllowed(inputFileRef.current.files[0].name)) {
                    setFormatError(formatHint || translations.pushImageFormat);
                    inputFileRef.current.value = '';
                    return;
                }
                setFormatError(null);
                const formData = new FormData();
                formData.append('file', inputFileRef.current.files[0]);

                API.uploadFile(formData, pathToCloudFolder)
                    .then((res) => {
                        resetValue(res.data.imageUrl);
                        setLoading(false);
                    })
                    .catch((e) => {
                        console.log(e);
                        setLoading(false);
                    });
            }
        };

        const getFromCloud = () => {
            API.getFiles(pathToCloudFolder)
                .then((res) => {
                    setUploads(res.data);
                    setModal(true);
                })
                .catch((e) => console.log('error', e));
        };
        return (
            <>
                <div className="Input">
                    {label && (
                        <div className="label">
                            <span>{label} </span>
                        </div>
                    )}
                    <div className="Input-File">
                        <input
                            ref={ref}
                            disabled={disabled}
                            readOnly={readOnly}
                            type="text"
                            {...restProps}
                        />

                        <input
                            ref={inputFileRef}
                            type="file"
                            accept={accept}
                            style={{ display: 'none' }}
                            onChange={handleUpload}
                        />
                        {!disabled && !readOnly && (
                            <div className="Input-File-Controls">
                                <Button
                                    title="Upload Local Files"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        inputFileRef.current.click();
                                    }}
                                    disabled={disabled}
                                    loading={loading}
                                >
                                    <FaUpload />
                                </Button>
                                <Button
                                    title="Uploaded On Cloud"
                                    disabled={disabled}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        getFromCloud();
                                    }}
                                >
                                    <FaCloud />
                                </Button>
                            </div>
                        )}
                    </div>
                    {!disabled && preferredFileSize && (
                        <div className="preferred-format">
                            {squareResolution
                                ? translations.imageSquareResolution
                                : preferredFileSize}
                        </div>
                    )}
                    {!disabled && allowedExtensions && (
                        <div className="preferred-format">
                            {formatHint || translations.pushImageFormat}
                        </div>
                    )}
                    <span className="error-msg">
                        {formatError && <p>{formatError}</p>}
                        {!formatError && error && <p>{error.message || translations.required}</p>}
                    </span>
                </div>
                {modal && (
                    <Modal
                        title="AWS S3 uploads"
                        ref={modalRef}
                        onClose={() => setModal(false)}
                        // maxWidth="800px"
                    >
                        <div className="S3-Itmes">
                            {uploads &&
                                uploads.length !== 0 &&
                                uploads.filter(isAllowed).map((item) =>
                                    item ? (
                                        <S3Item
                                            key={item}
                                            path={item}
                                            onClick={(source) => {
                                                setFormatError(null);
                                                resetValue(source);
                                                setModal(false);
                                            }}
                                        />
                                    ) : null,
                                )}
                        </div>
                    </Modal>
                )}
            </>
        );
    },
);

export default InputFile;
